// @flow

import React, {type Node, useState} from 'react';
import { IfFeature } from '@tectonics/prs/feature';
import { useAllFeatures } from '@tectonics/prs/payment-spec';
import styled from 'styled-components';
import {
  getAllAllowedCryptocurrencyTypes, getCryptocurrencyFromPaymentTypeCode,
} from '@tectonics/commons/cryptocurrency/cryptocurrencies/cryptocurrencies';
import {useOpenedIncomingPayment} from '@tectonics/prs/incoming-payment';
import {useUserInputValidationError} from '@tectonics/prs/validation';
import {useFormHasBeenSent} from '@tectonics/prs/form-status';
import {useSubmitting} from '@tectonics/prs/submitting';
import type {Cryptocurrency} from '@tectonics/commons/cryptocurrency/cryptocurrencies/cryptocurrency-type';
import type {DropdownOption} from '@tectonics/look-and-feel/volcanes/molecules/dropdown/dropdown-option';
import CryptocurrencyDropdown from './dropdowns/cryptocurrency-dropdown';
import ProtocolDropdown from './dropdowns/protocol-dropdown';
import {useUserInputValue} from '@tectonics/prs/user-input-values';
import {
  ValidationHint,
} from '@tectonics/look-and-feel/volcanes/pages/page-opened-payment/payment-method/cryptocurrency-form/validation-hint';
import ProtocolWarning from '@tectonics/look-and-feel/volcanes/pages/page-opened-payment/payment-method/cryptocurrency-form/protocol-warning/protocol-warning';
import {
  FieldCustomerBirthdate,
} from '@tectonics/look-and-feel/volcanes/pages/page-opened-payment/customer-form/field-customer-birthdate';
import {
  DisclaimerTermsConditions,
} from '@tectonics/look-and-feel/volcanes/pages/page-opened-payment/payment-method/cryptocurrency-form/disclaimer-terms-conditions';

const emptyOption: DropdownOption = {value: '', text: ''};

export function CryptocurrencyForm(): Node {
  const allFeatures = useAllFeatures();
  const incomingPayment = useOpenedIncomingPayment();
  const allowedCryptocurrenciesTypes = getAllAllowedCryptocurrencyTypes(incomingPayment);

  // eslint-disable-next-line no-unused-vars
  const [selectedCryptocurrencyCode, setSelectedCryptocurrencyCode] = useUserInputValue('selectedCryptocurrency');
  const selectedCryptocurrencyValidationError = useUserInputValidationError('selectedCryptocurrency');
  const [selectedCryptocurrency, setSelectedCryptocurrency] = useState<?Cryptocurrency>();
  const [cryptocurrencyIsOpen, setCryptocurrencyIsOpen] = useState(false);
  const [selectedCryptocurrencyOption, setSelectedCryptocurrencyOption] = useState<DropdownOption>(emptyOption);

  // eslint-disable-next-line no-unused-vars
  const [selectedProtocolCode, setSelectedProtocolCode] = useUserInputValue('selectedProtocol');
  const selectedProtocolValidationError = useUserInputValidationError('selectedProtocol');
  const [protocolIsOpen, setProtocolIsOpen] = useState(false);
  const [selectedProtocolOption, setSelectedProtocolOption] = useState<DropdownOption>(emptyOption);

  const formHasBeenSent = useFormHasBeenSent();
  const submitting = useSubmitting();

  if (!allowedCryptocurrenciesTypes.length) return null;

  const handleCryptocurrencySelect = (cryptocurrency) => {
    setSelectedCryptocurrencyOption({
      value: cryptocurrency.code,
      text: cryptocurrency.name,
      icon: cryptocurrency.icon,
    });
    setSelectedCryptocurrencyCode(cryptocurrency.code);
    setSelectedCryptocurrency(getCryptocurrencyFromPaymentTypeCode(cryptocurrency.code));
    setCryptocurrencyIsOpen(false);

    setSelectedProtocolOption(emptyOption);
    setSelectedProtocolCode('');
  };

  const handleProtocolSelect = (protocol) => {
    setSelectedProtocolOption({
      value: protocol.code,
      text: protocol.name,
      icon: protocol.icon,
    });
    setSelectedProtocolCode(protocol.code);
    setProtocolIsOpen(false);
  };

  return(
    <IfFeature allFeatures={allFeatures} wanted="display_cryptocurrency_accepts_brand">
      <DisclaimerTermsConditions/>
      <FieldCustomerBirthdate />
      <Wrapper>
        <DropdownWrapper data-test-name="cryptocurrency_dropdown">
          <CryptocurrencyDropdown
            allowedCryptocurrenciesTypes={allowedCryptocurrenciesTypes}
            selectedCryptocurrencyOption={selectedCryptocurrencyOption}
            cryptocurrencyIsOpen={cryptocurrencyIsOpen}
            setCryptocurrencyIsOpen={setCryptocurrencyIsOpen}
            submitting={submitting}
            onCryptocurrencySelect={handleCryptocurrencySelect}
          />
          {formHasBeenSent && selectedCryptocurrencyValidationError && (
            <ValidationHint error={selectedCryptocurrencyValidationError} fieldName="cryptocurrency" />
          )}
        </DropdownWrapper>
        <DropdownWrapper data-test-name="protocol_dropdown">
          <ProtocolDropdown
            selectedCryptocurrency={selectedCryptocurrency}
            selectedProtocolOption={selectedProtocolOption}
            protocolIsOpen={protocolIsOpen}
            setProtocolIsOpen={setProtocolIsOpen}
            setSelectedProtocolOption={setSelectedProtocolOption}
            setSelectedProtocolCode={setSelectedProtocolCode}
            submitting={submitting}
            onProtocolSelect={handleProtocolSelect}
          />
          {formHasBeenSent && selectedProtocolValidationError && (
            <ValidationHint error={selectedProtocolValidationError} fieldName="network" />
          )}
        </DropdownWrapper>
      </Wrapper>
      <ProtocolWarning />
    </IfFeature>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;
  @media (max-width: 500px) {
    flex-direction: column;
  }
`;

const DropdownWrapper = styled.div`
  display: flex;
  gap: 8px;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  flex: 1 0 0;
`;
